import type { ReactNode, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function BaseIcon({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.55rem"
      height="1.55rem"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

const ICONS: Record<string, (props: IconProps) => ReactNode> = {
  home: (props) => (
    <BaseIcon {...props}>
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" />
    </BaseIcon>
  ),
  menu: (props) => (
    <BaseIcon {...props}>
      <path d="M5 7h14M5 12h14M5 17h10" />
      <circle cx="19" cy="17" r="1.2" fill="currentColor" stroke="none" />
    </BaseIcon>
  ),
  orders: (props) => (
    <BaseIcon {...props}>
      <path d="M7 4h10l1 16H6L7 4Z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </BaseIcon>
  ),
  tables: (props) => (
    <BaseIcon {...props}>
      <ellipse cx="12" cy="8" rx="7" ry="3" />
      <path d="M5 8v4c0 1.7 3.1 3 7 3s7-1.3 7-3V8" />
      <path d="M7 15v3M17 15v3" />
    </BaseIcon>
  ),
  reservations: (props) => (
    <BaseIcon {...props}>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 11h16" />
      <path d="M9 15h6" />
    </BaseIcon>
  ),
  haccp: (props) => (
    <BaseIcon {...props}>
      <path d="M12 3 4.5 6v5c0 5 3.2 8.5 7.5 10 4.3-1.5 7.5-5 7.5-10V6L12 3Z" />
      <path d="M9.5 12.2 11.2 14l3.5-4" />
    </BaseIcon>
  ),
  support: (props) => (
    <BaseIcon {...props}>
      <path d="M7 11a5 5 0 0 1 10 0v2a3 3 0 0 0 3 3h-1" />
      <path d="M7 13H6a3 3 0 0 0-3 3" />
      <path d="M10 20h4" />
    </BaseIcon>
  ),
  hello: (props) => (
    <BaseIcon {...props}>
      <circle cx="12" cy="12" r="8" />
      <path d="M8.5 13.5c1 1.4 2.6 2.2 3.5 2.2s2.5-.8 3.5-2.2" />
      <path d="M9 10h.01M15 10h.01" />
    </BaseIcon>
  ),
  master: (props) => (
    <BaseIcon {...props}>
      <circle cx="12" cy="8" r="3" />
      <path d="M5 19c1.5-3 4-4.5 7-4.5S17.5 16 19 19" />
      <path d="M17 4.5 19 6l-2 1.5" />
    </BaseIcon>
  ),
  collapse: (props) => (
    <BaseIcon {...props}>
      <path d="M15 6 9 12l6 6" />
    </BaseIcon>
  ),
  expand: (props) => (
    <BaseIcon {...props}>
      <path d="M9 6l6 6-6 6" />
    </BaseIcon>
  ),
  // ── Master Console 2.0 icons ──
  dashboard: (props) => (
    <BaseIcon {...props}>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </BaseIcon>
  ),
  tenants: (props) => (
    <BaseIcon {...props}>
      <path d="M3 21h18M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h.01M15 9h.01" />
    </BaseIcon>
  ),
  list: (props) => (
    <BaseIcon {...props}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="3.5" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="18" r="1" fill="currentColor" stroke="none" />
    </BaseIcon>
  ),
  plus: (props) => (
    <BaseIcon {...props}>
      <path d="M12 5v14M5 12h14" />
    </BaseIcon>
  ),
  users: (props) => (
    <BaseIcon {...props}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
      <path d="M16 11a3 3 0 0 0 0-6" />
      <path d="M18 15c2.5 0.5 3 2.5 3 5" />
    </BaseIcon>
  ),
  star: (props) => (
    <BaseIcon {...props}>
      <path d="M12 3l2.5 6 6.5.5-5 4.5 1.5 6.5L12 17l-5.5 3.5L8 14 3 9.5l6.5-.5L12 3Z" />
    </BaseIcon>
  ),
  catalog: (props) => (
    <BaseIcon {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </BaseIcon>
  ),
  plans: (props) => (
    <BaseIcon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 4v5M16 4v5" />
    </BaseIcon>
  ),
  modules: (props) => (
    <BaseIcon {...props}>
      <path d="M12 3 3 7.5v9L12 21l9-4.5v-9L12 3Z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </BaseIcon>
  ),
  sms: (props) => (
    <BaseIcon {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 10h4M7 14h6" />
      <path d="M17 15l3 2v-4" />
    </BaseIcon>
  ),
  billing: (props) => (
    <BaseIcon {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9v.01M18 15v.01" />
    </BaseIcon>
  ),
  inbox: (props) => (
    <BaseIcon {...props}>
      <path d="M3 13v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6" />
      <path d="M3 13l3-8h12l3 8" />
      <path d="M3 13h4l2 3h6l2-3h4" />
    </BaseIcon>
  ),
  alert: (props) => (
    <BaseIcon {...props}>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 9v5M12 17v.01" />
    </BaseIcon>
  ),
  portale: (props) => (
    <BaseIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </BaseIcon>
  ),
  config: (props) => (
    <BaseIcon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </BaseIcon>
  ),
  audit: (props) => (
    <BaseIcon {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </BaseIcon>
  ),
  gym: (props) => (
    <BaseIcon {...props}>
      <path d="M4 9v6M20 9v6M2 11v2M22 11v2M7 7v10M17 7v10M7 12h10" />
    </BaseIcon>
  ),
  beauty: (props) => (
    <BaseIcon {...props}>
      <path d="M12 3c-3 0-5 2-5 5 0 2 1 3 1 5s-1 3-1 5c0 1 1 3 5 3s5-2 5-3c0-2-1-3-1-5s1-3 1-5c0-3-2-5-5-5Z" />
      <path d="M9 8h6" />
    </BaseIcon>
  ),
  restaurant: (props) => (
    <BaseIcon {...props}>
      <path d="M7 3v8a2 2 0 0 0 4 0V3M9 3v18M16 3c-2 0-3 2-3 5s1 4 3 4v9" />
    </BaseIcon>
  ),
  default: (props) => (
    <BaseIcon {...props}>
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </BaseIcon>
  ),
};

export function NavIcon({ name, ...props }: IconProps & { name: string }) {
  const key = String(name || 'default').toLowerCase();
  const Icon = ICONS[key] || ICONS.default;
  return <Icon {...props} />;
}
